/**
 * Contrato del registro manual de Rastreo: equipos, SIM, vínculo con placa y cliente.
 *
 * El GPS solo transmite su identificador y su telemetría. Lo que aquí se registra
 * es lo que el equipo NO manda: modelo, SIM, ciudad, placa y de quién es
 * (especificación `specs/rastreo-registro/`, RR-04).
 *
 * Un solo esquema para el navegador y para el servidor (RR-05): el formulario
 * marca el error debajo del campo y el Worker vuelve a validar lo mismo, porque
 * una validación que solo vive en la pantalla no protege nada.
 *
 * Los esquemas de entrada **normalizan** antes de validar —placa en mayúsculas
 * sin guiones, teléfono sin +57 ni espacios, documento sin puntos—: así «abc-123»
 * y «ABC123» no terminan siendo dos vehículos.
 */

import { z } from "zod";
import { isCalendarDate } from "../rastreo/fechas.ts";
import { normalizeDocument, normalizePhone, normalizePlate } from "../rastreo/normalize.ts";

/* --------------------------------------------------------------- *
 * Catálogos
 * --------------------------------------------------------------- */

/** Los protocolos que decodifica el motor (0.1.0), más «por confirmar» para un modelo nuevo. */
export const RASTREO_PROTOCOLS = ["gt06", "teltonika", "h02", "tk103", "por_confirmar"] as const;
export type RastreoProtocol = (typeof RASTREO_PROTOCOLS)[number];

export const PROTOCOL_LABEL: Record<RastreoProtocol, string> = {
  gt06: "GT06 (Concox, Jimi IoT, ProTrack y clones)",
  teltonika: "Teltonika (FMB, FMC, FMM, FMT)",
  h02: "H02 (Sinotrack y similares)",
  tk103: "TK103 (Coban)",
  por_confirmar: "Por confirmar",
};

export const SIM_OPERATORS = ["CLARO", "MOVISTAR", "TIGO", "WOM", "OTRO"] as const;
export type SimOperator = (typeof SIM_OPERATORS)[number];

export const SIM_OPERATOR_LABEL: Record<SimOperator, string> = {
  CLARO: "Claro",
  MOVISTAR: "Movistar",
  TIGO: "Tigo",
  WOM: "WOM",
  OTRO: "Otro",
};

export const DOCUMENT_TYPES = ["CC", "CE", "NIT", "PP", "TI"] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const DOCUMENT_TYPE_LABEL: Record<DocumentType, string> = {
  CC: "Cédula de ciudadanía",
  CE: "Cédula de extranjería",
  NIT: "NIT",
  PP: "Pasaporte",
  TI: "Tarjeta de identidad",
};

/**
 * Modelos que ya conoce el motor (`rastreo/motor/perfiles/modelos.json`), con su
 * protocolo. Son sugerencias para el formulario, no una lista cerrada: un modelo
 * nuevo se escribe a mano y entra con protocolo «por confirmar».
 */
export const KNOWN_GPS_MODELS: ReadonlyArray<{ code: string; protocol: RastreoProtocol }> = [
  { code: "GT06-0x8039", protocol: "gt06" },
  { code: "BL03", protocol: "gt06" },
  { code: "PT08F", protocol: "gt06" },
  { code: "BT08F", protocol: "por_confirmar" },
  { code: "GS-G10", protocol: "por_confirmar" },
  { code: "BOXTRACK", protocol: "por_confirmar" },
];

/** Sugerencias de ciudad. Se puede escribir cualquier otra. */
export const SUGGESTED_CITIES = [
  "Pereira",
  "Dosquebradas",
  "Santa Rosa de Cabal",
  "Manizales",
  "Armenia",
  "Bogotá",
  "Medellín",
  "Cali",
  "Barranquilla",
  "Cartagena",
  "Bucaramanga",
  "Cúcuta",
  "Ibagué",
  "Villavicencio",
  "Pasto",
  "Neiva",
  "Santa Marta",
  "Montería",
  "Tunja",
  "Popayán",
] as const;

/* --------------------------------------------------------------- *
 * Piezas de validación
 * --------------------------------------------------------------- */

/** Texto obligatorio: vacío pide escribirlo; corto o largo dice el límite. */
const trimmed = (min: number, max: number, label: string) =>
  z
    .string({ error: `Escribe ${label}.` })
    .trim()
    .min(1, { error: `Escribe ${label}.` })
    .min(min, { error: `${capitalize(label)}: mínimo ${min} caracteres.` })
    .max(max, { error: `${capitalize(label)}: máximo ${max} caracteres.` });

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "" y null valen lo mismo: el campo opcional quedó vacío. */
const emptyToNull = (value: unknown) =>
  value === undefined || value === null || (typeof value === "string" && value.trim() === "") ? null : value;

const optional = <T extends z.ZodType>(schema: T) => z.preprocess(emptyToNull, schema.nullable());

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;

/** Placa colombiana: AAA999 (carro) o AAA99A (moto). */
export const plateSchema = z
  .string()
  .transform(normalizePlate)
  .pipe(z.string().regex(/^[A-Z]{3}\d{2}[A-Z0-9]$/u, { error: "La placa va como ABC123 (carro) o ABC12D (moto)." }));

/** Celular (3xx) o fijo nacional (60x), 10 dígitos. Acepta +57, espacios y guiones. */
export const phoneSchema = z
  .string({ error: "Escribe el teléfono." })
  .transform(normalizePhone)
  .pipe(
    z.string().regex(/^(3\d{9}|60\d{8})$/u, { error: "El teléfono va con 10 dígitos: celular 3xx o fijo 60x." }),
  );

/** Línea de la SIM del GPS: siempre un celular de 10 dígitos. */
const simNumberSchema = z
  .string({ error: "Escribe el número de la SIM." })
  .transform(normalizePhone)
  .pipe(z.string().regex(/^3\d{9}$/u, { error: "El número de la SIM va con 10 dígitos y empieza por 3." }));

const iccidSchema = z
  .string()
  .transform((value) => value.replace(/[\s-]/gu, ""))
  .pipe(z.string().regex(/^89\d{17,18}$/u, { error: "El ICCID tiene 19 o 20 dígitos y empieza por 89." }));

/** Fecha de un `<input type="date">`. Que no sea futura lo revisa el servidor, que tiene reloj. */
/** Un día que existe: `2026-02-31` no se corre a marzo, se rechaza. */
const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, { error: "Fecha inválida." })
  .refine(isCalendarDate, { error: "Esa fecha no existe en el calendario." });

const policySchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9\-/.]{2,29}$/u, {
    error: "La póliza va con 3 a 30 letras, números, guiones, barras o puntos.",
  });

/* --------------------------------------------------------------- *
 * Cliente
 * --------------------------------------------------------------- */

export const clienteDatosSchema = z
  .object({
    nombre: trimmed(3, 120, "el nombre del cliente"),
    tipoDocumento: z.enum(DOCUMENT_TYPES, { error: "Elige el tipo de documento." }),
    // La forma general aquí, para que el error salga aunque otro campo también falle; la regla
    // propia de cada tipo de documento va en el `superRefine` de abajo.
    documento: z
      .string({ error: "Escribe el número de documento." })
      .transform(normalizeDocument)
      .pipe(z.string().regex(/^[A-Z0-9-]{5,15}$/u, { error: "El documento va con 5 a 15 letras o números." })),
    telefono: phoneSchema,
    direccion: trimmed(5, 160, "la dirección"),
    ciudad: trimmed(2, 60, "la ciudad del cliente"),
    poliza: optional(policySchema),
  })
  .superRefine((cliente, context) => {
    const rule = DOCUMENT_RULE[cliente.tipoDocumento];
    if (!rule.pattern.test(cliente.documento)) {
      context.addIssue({ code: "custom", path: ["documento"], message: rule.message });
    }
  });
export type ClienteDatos = z.infer<typeof clienteDatosSchema>;

const DOCUMENT_RULE: Record<DocumentType, { pattern: RegExp; message: string }> = {
  CC: { pattern: /^\d{5,10}$/u, message: "La cédula va solo con números, de 5 a 10 dígitos." },
  TI: { pattern: /^\d{8,11}$/u, message: "La tarjeta de identidad va solo con números, de 8 a 11 dígitos." },
  NIT: { pattern: /^\d{9}(-\d)?$/u, message: "El NIT va con 9 dígitos y, si quieres, el dígito de verificación: 900123456-7." },
  CE: { pattern: /^[A-Z0-9]{5,15}$/u, message: "La cédula de extranjería va con 5 a 15 letras o números." },
  PP: { pattern: /^[A-Z0-9]{5,15}$/u, message: "El pasaporte va con 5 a 15 letras o números." },
};

const clienteSeleccionSchema = z.discriminatedUnion("modo", [
  z.object({ modo: z.literal("ninguno") }),
  z.object({ modo: z.literal("existente"), id: z.string().min(1, { error: "Elige el cliente." }) }),
  z.object({ modo: z.literal("nuevo"), datos: clienteDatosSchema }),
  /** Cambia los datos del cliente que ya tiene el equipo y lo deja vinculado. */
  z.object({ modo: z.literal("editar"), id: z.string().min(1), datos: clienteDatosSchema }),
]);
export type ClienteSeleccion = z.infer<typeof clienteSeleccionSchema>;

/* --------------------------------------------------------------- *
 * Equipo
 * --------------------------------------------------------------- */

const equipoBase = z.object({
  nombre: trimmed(2, 60, "el nombre del equipo"),
  modelo: trimmed(2, 40, "el modelo"),
  protocolo: z.enum(RASTREO_PROTOCOLS, { error: "Elige el protocolo." }),
  /**
   * El equipo del motor que se está registrando, cuando se llega desde «Registrar este
   * equipo». Sin IMEI escrito, el motor pone el suyo (D28). Con IMEI escrito, el motor comprueba
   * que sea el de ese equipo y, si no, no guarda nada. Vacío al agregar con «+».
   */
  motorDeviceId: optional(z.string().regex(UUID, { error: "Equipo del motor inválido." })),
  simNumero: simNumberSchema,
  simOperador: z.enum(SIM_OPERATORS, { error: "Elige el operador." }),
  simIccid: optional(iccidSchema),
  ciudad: trimmed(2, 60, "la ciudad"),
  placa: optional(plateSchema),
  instaladoEn: optional(dateSchema),
  notas: optional(z.string().trim().max(500, { error: "Las notas van con máximo 500 caracteres." })),
  cliente: clienteSeleccionSchema,
});

/**
 * El identificador depende del protocolo: GT06 y Teltonika mandan el IMEI de 15
 * dígitos; H02 y TK103 pueden mandar un id más corto.
 */
function checkIdentifier(identificador: string, protocolo: RastreoProtocol, context: z.RefinementCtx) {
  const imei = protocolo === "gt06" || protocolo === "teltonika";
  const ok = imei ? /^\d{15}$/u.test(identificador) : /^[0-9A-Za-z]{6,20}$/u.test(identificador);
  if (!ok) {
    context.addIssue({
      code: "custom",
      path: ["identificador"],
      message: imei
        ? "Con este protocolo el identificador es el IMEI: 15 dígitos."
        : "El identificador va con 6 a 20 letras o números.",
    });
  }
}

/** Sin espacios ni guiones. La forma general aquí; la del protocolo, en `checkIdentifier`. */
const identifierInput = z
  .string({ error: "Escribe el IMEI del equipo." })
  .transform((value) => value.replace(/[\s-]/gu, ""))
  .pipe(
    z
      .string()
      .min(1, { error: "Escribe el IMEI del equipo." })
      .regex(/^[0-9A-Za-z]{6,20}$/u, { error: "El identificador va con 6 a 20 letras o números." }),
  );

/**
 * El IMEI sale de un solo lugar (RR-23, D28 del motor):
 * - escrito, al agregar con «+» un equipo que todavía no transmite;
 * - de la trama, al registrar uno de «Transmitiendo sin registrar» (`tramaRef`);
 * - del motor, al registrar un equipo que el motor ya tiene (`motorDeviceId` sin IMEI).
 * En los dos últimos el navegador nunca lo tiene completo (RR-07): lo resuelve el motor.
 */
export const crearEquipoSchema = equipoBase
  .extend({
    identificador: z.preprocess(emptyToNull, identifierInput.nullable()),
    /** La trama en cuarentena que trajo el IMEI: el `ref` de la lista de pendientes. */
    tramaRef: optional(z.string().regex(/^\d{1,18}$/u, { error: "Trama inválida." })),
  })
  .superRefine(
    (equipo, context) => {
      // Corre aunque otros campos fallen: «Escribe el IMEI» tiene que salir junto con los demás
      // errores, como cuando el IMEI era obligatorio. Por eso lee los valores sin suponer su forma.
      if (typeof equipo !== "object" || equipo === null) return;
      const presente = (value: unknown) => value !== null && value !== undefined && value !== "";
      const escrito = presente(equipo.identificador);
      if (presente(equipo.tramaRef) && (escrito || presente(equipo.motorDeviceId))) {
        context.addIssue({
          code: "custom",
          path: ["identificador"],
          message: "El IMEI se toma de la trama: no se escribe ni se elige otro equipo.",
        });
      } else if (!escrito && !presente(equipo.tramaRef) && !presente(equipo.motorDeviceId)) {
        context.addIssue({ code: "custom", path: ["identificador"], message: "Escribe el IMEI del equipo." });
      }
    },
    { when: () => true },
  )
  .superRefine((equipo, context) => {
    if (equipo.identificador !== null) checkIdentifier(equipo.identificador, equipo.protocolo, context);
  });
export type CrearEquipoInput = z.infer<typeof crearEquipoSchema>;
/** Lo que el formulario manda, antes de normalizar. */
export type CrearEquipoForm = z.input<typeof crearEquipoSchema>;

/** Al editar, el IMEI vacío conserva el registrado: la pantalla nunca lo tiene completo. */
export const editarEquipoSchema = equipoBase
  .extend({ identificador: z.preprocess(emptyToNull, identifierInput.nullable()) })
  .superRefine((equipo, context) => {
    if (equipo.identificador !== null) checkIdentifier(equipo.identificador, equipo.protocolo, context);
  });
export type EditarEquipoInput = z.infer<typeof editarEquipoSchema>;
export type EditarEquipoForm = z.input<typeof editarEquipoSchema>;

/* --------------------------------------------------------------- *
 * Lo que el Worker entrega
 * --------------------------------------------------------------- */

export const registroClienteSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  tipoDocumento: z.enum(DOCUMENT_TYPES),
  documento: z.string(),
  telefono: z.string(),
  direccion: z.string(),
  ciudad: z.string(),
  poliza: z.string().nullable(),
  actualizadoEn: z.string(),
});
export type RegistroCliente = z.infer<typeof registroClienteSchema>;

export const registroEquipoSchema = z.object({
  id: z.string(),
  nombre: z.string(),
  /** Solo los últimos 4 caracteres del IMEI (RR-07). El completo no sale del Worker. */
  identificadorFinal: z.string().max(4),
  modelo: z.string(),
  protocolo: z.enum(RASTREO_PROTOCOLS),
  motorDeviceId: z.string().nullable(),
  simNumero: z.string(),
  simOperador: z.enum(SIM_OPERATORS),
  simIccid: z.string().nullable(),
  ciudad: z.string(),
  instaladoEn: z.string().nullable(),
  notas: z.string().nullable(),
  /** Vínculo vigente: placa y cliente de hoy, y desde cuándo. */
  placa: z.string().nullable(),
  clienteId: z.string().nullable(),
  vigenteDesde: z.string().nullable(),
  creadoEn: z.string(),
  actualizadoEn: z.string(),
});
export type RegistroEquipo = z.infer<typeof registroEquipoSchema>;

/**
 * Lo que Sync/Softseguros, HighLevel y Alegra dicen de una placa, tal como lo consolida la
 * carga de atributos de Drive (`vehicle_attribute`). La placa es la llave que comparten todos
 * esos sistemas; por eso la ficha manual se relaciona con ellos por la placa y no por el
 * documento ni por el nombre. Cada campo trae de qué sistema salió y cuándo se leyó.
 */
export const relacionPlacaSchema = z.object({
  placa: z.string(),
  aseguradora: z.string().nullable(),
  numeroPoliza: z.string().nullable(),
  titularNombre: z.string().nullable(),
  titularDocumento: z.string().nullable(),
  marca: z.string().nullable(),
  linea: z.string().nullable(),
  modeloAnio: z.string().nullable(),
  ciudad: z.string().nullable(),
  /** Por campo: sistema de origen y momento de la lectura. */
  fuentes: z.record(z.string(), z.object({ system: z.string(), observedAt: z.string() })),
  actualizadoEn: z.string(),
});
export type RelacionPlaca = z.infer<typeof relacionPlacaSchema>;

export const rastreoRegistryResponseSchema = z.object({
  equipos: z.array(registroEquipoSchema),
  clientes: z.array(registroClienteSchema),
  /** Solo de las placas que tienen un vínculo vigente en el registro. */
  relaciones: z.array(relacionPlacaSchema),
});
export type RastreoRegistryResponse = z.infer<typeof rastreoRegistryResponseSchema>;

/**
 * Un GPS que ya apunta al servidor y nadie registró (RR-23, D28 del motor): sus tramas están en
 * cuarentena. Del IMEI llegan solo los últimos 4 dígitos, para compararlos con la etiqueta.
 */
export const rastreoPendienteSchema = z.object({
  /** Opaca: con ella el motor toma el IMEI de la trama al registrar. */
  ref: z.string().regex(/^\d{1,18}$/u),
  identificadorFinal: z.string().max(4),
  /** El del puerto por el que llegó; `null` si el motor manda uno que Drive no conoce. */
  protocolo: z.enum(RASTREO_PROTOCOLS).nullable(),
  primeraTrama: z.string(),
  ultimaTrama: z.string(),
  tramas: z.number().int().nonnegative(),
});
export type RastreoPendiente = z.infer<typeof rastreoPendienteSchema>;

export const rastreoPendientesResponseSchema = z.object({
  /** `false` si el motor todavía no tiene la ruta (se desplegó Drive antes): la lista va vacía. */
  disponible: z.boolean(),
  items: z.array(rastreoPendienteSchema),
});
export type RastreoPendientesResponse = z.infer<typeof rastreoPendientesResponseSchema>;

/**
 * Qué pasó en el motor al guardar:
 * - `registrado`: el IMEI no estaba y quedó registrado; sus tramas dejan la cuarentena;
 * - `actualizado`: ya estaba y se le actualizaron nombre, modelo y protocolo;
 * - `sin_cambios`: la edición no tocó nada que el motor guarde.
 *
 * No hay un estado «se guardó sin el motor»: sin motor, el alta falla cerrada con 503 (RR-16).
 */
export const MOTOR_REGISTRO = ["registrado", "actualizado", "sin_cambios"] as const;

export const saveEquipoResponseSchema = z.object({
  equipo: registroEquipoSchema,
  /** `true` si cambió la placa o el cliente y se abrió un vínculo nuevo. */
  vinculoCambiado: z.boolean(),
  motor: z.enum(MOTOR_REGISTRO),
});
export type SaveEquipoResponse = z.infer<typeof saveEquipoResponseSchema>;
